import { App, Form } from "antd";
import { InsuranceSearchTab } from "./SearchFlow/InsuranceSearchTab";
import { MergeFlowModalContent } from "./MergeFlow/MergeFlowModalContent";
import { PatientSearchTab, type SearchFlowModalContentProps } from "./SearchFlow/PatientSearchTab";
import { PayNowModalContent } from "../PayNowModal/PayNowModal";
import { PaymentForm } from "./PaymentForm/PaymentForm";
import { ProvisionSpin, ProvisionSpinModal } from "./ProvisionSpin";
import { SearchFlowContextProvider } from "./SearchFlow/SearchFlowContextProvider";
import { TakePaymentFooter } from "./TakePaymentRequest/TakePaymentFooter";
import { TransactorKind } from "@vyne-shared/components/Payment/transactorKind";
import { create, useModal } from "@ebay/nice-modal-react";
import { displaySubmissionError } from "./PaymentRequest/displaySubmissionError";
import { useManualProvisioningSubmission } from "./TakePaymentRequest/useManualProvisioningSubmission";
import { useMasonFlag } from "utils/useMasonFlag";
import { useProvisionTransactor } from "api/clients/useProvisionTransactor";
import { useSearchFlowContext } from "./SearchFlow/useSearchFlow";
import { useState } from "react";
import type { PatientUpdateForm } from "api/requests";

export const TakePaymentModal = create(() => {
    const pmsIntegrated = useMasonFlag();

    return (
        <SearchFlowContextProvider>
            {pmsIntegrated ? <TakePaymentModalContentPms /> : <TakePaymentModalContentNonPms />}
        </SearchFlowContextProvider>
    );
});

export const TakePaymentModalByPmsId = create<{ readonly pmsId: string }>(({ pmsId }) => {
    const modal = useModal();
    const { data: patientId, isPending: isProvisioning } = useProvisionTransactor(pmsId, undefined);

    return patientId ? (
        <PayNowModalContent modal={modal} patientId={patientId} />
    ) : (
        /* Auto-Provisioning P/RP based on VyneSync patient... */
        <ProvisionSpinModal modal={modal} isProvisioning={isProvisioning} />
    );
});

const TakePaymentModalContentPms = () => {
    const { searchComplete, selectedTransactor, isProvisioning } = useSearchFlowContext();

    if (searchComplete && !selectedTransactor) {
        throw new Error("integrated take-payment does not support manually entering a new P/RP");
    }

    return (
        <TakePaymentModalContent footer={false}>
            {/* Auto-Provisioning P/RP based on VyneSync patient... */}
            <ProvisionSpin isProvisioning={isProvisioning} />
        </TakePaymentModalContent>
    );
};

const TakePaymentModalContentNonPms = () => {
    const { modal: errorModal } = App.useApp();
    const { modal } = useSearchFlowContext();

    // form used to collect details for a manually provisioned P/RP
    const [paymentForm] = Form.useForm<PatientUpdateForm>();

    // Manually provision Payments P/RP using information supplied on the data entry form
    const { isPending, submit, data } = useManualProvisioningSubmission(paymentForm, {
        onError: (err) => displaySubmissionError(errorModal, err, modal),
    });

    // if user chose to manually provision a new patient, let's use manuallyProvisionedPatientId
    const manuallyProvisionedPatientId = data;

    return (
        <TakePaymentModalContent
            manuallyProvisionedPatientId={manuallyProvisionedPatientId}
            footer={<TakePaymentFooter isPending={isPending} onContinue={submit} />}
        >
            {/* PaymentForm is acting more like patient-provisioning-form here */}
            <PaymentForm form={paymentForm} existingPrp={undefined} hideMessaging hideBalance />
        </TakePaymentModalContent>
    );
};

type TakePaymentModalContentProps = Readonly<React.PropsWithChildren> & {
    manuallyProvisionedPatientId?: number;
    readonly footer?: React.ReactNode;
};

/**
 * Show merge content, or pay-now content, or search flow (which includes an interactive search portion and a
 * provisioning portion).
 *
 * Both integrated and non-integrated follow a common pattern: if we need to merge, do that first; if we have a
 * provisioned Payments P/RP, display Pay Now; otherwise, we still have to select a patient, so display that flow.
 *
 * Supplied children shown only if we're in the provisioning portion of a search flow.
 * Supplied footer shown only if we're in a search flow.
 *
 * Consider re-typing/refactoring for clarity.
 */
const TakePaymentModalContent = ({ footer, children, manuallyProvisionedPatientId }: TakePaymentModalContentProps) => {
    const { merging } = useSearchFlowContext();

    return merging ? (
        <MergeFlowModalContent />
    ) : (
        <TakePaymentTabs
            footer={footer}
            children={children}
            manuallyProvisionedPatientId={manuallyProvisionedPatientId}
        />
    );
};

interface TakePaymentTabsProps extends Omit<SearchFlowModalContentProps, "title" | "setTakePaymentTab"> {
    manuallyProvisionedPatientId?: number;
}

export type TakePaymentTabType = "patient-payment" | "insurance-payment";

const TakePaymentTabs = ({ footer, children, manuallyProvisionedPatientId }: TakePaymentTabsProps) => {
    const [takePaymentTab, setTakePaymentTab] = useState<TakePaymentTabType>("patient-payment");
    const provisionedTransactorId = useProvisionedTransactorId(manuallyProvisionedPatientId);
    const { modal, onReset } = useSearchFlowContext();

    if (provisionedTransactorId) {
        return (
            <PayNowModalContent
                modal={modal}
                patientId={provisionedTransactorId}
                setTakePaymentTab={(newTab: TakePaymentTabType) => {
                    onReset();
                    setTakePaymentTab(newTab);
                }}
                takePaymentTab={takePaymentTab}
            />
        );
    }

    return takePaymentTab === "patient-payment" ? (
        <PatientSearchTab title="Take a Payment" showRecentVisits footer={footer} setTakePaymentTab={setTakePaymentTab}>
            {/* children shown while auto-provisioning P/RP, or manually collecting data to provision P/RP */}
            {children}
        </PatientSearchTab>
    ) : (
        <InsuranceSearchTab setTakePaymentTab={setTakePaymentTab} />
    );
};

function useProvisionedTransactorId(manuallyProvisionedPatientId: number | undefined) {
    const [pmsId, transactorId, insuranceCarrierName] = useTransactorProvisioningFacts();
    // Automatically provision Payments P/RP using information from Vyne Sync once a patient is selected.
    // Or, provision a Payments Insurance Carrier using the selected insurance carrier name.
    // Prior to patient selection and provisioning, provisionedTransactorId remains undefined.
    const { data: provisionedTransactorId } = useProvisionTransactor(
        pmsId,
        // note that if this argument is supplied and truthy, useProvisionTransactor just returns it without calling a remote API
        transactorId ?? manuallyProvisionedPatientId,
        insuranceCarrierName,
    );
    return provisionedTransactorId;
}

function useTransactorProvisioningFacts() {
    const { merging, searchComplete, selectedTransactor } = useSearchFlowContext();

    if (!merging && searchComplete && selectedTransactor) {
        const { kind, transactor } = selectedTransactor;
        switch (kind) {
            case TransactorKind.Patient:
                return [transactor.pmsId, transactor.id, undefined] as const;
            case TransactorKind.InsuranceCarrier:
                return [undefined, transactor.id, transactor.name] as const;
        }
    }
    return [undefined, undefined, undefined] as const;
}
